import React, { useState } from 'react';
import ImageUploader from '../../components/ImageUploader';
import ServiceContentTab from './tabs/ServiceContentTab';
import ServiceFaqTab from './tabs/ServiceFaqTab';
import ServiceWorkTab from './tabs/ServiceWorkTab';
import ServiceSeoTab from './tabs/ServiceSeoTab';

const generateSlug = (text) => text
  .toLowerCase()
  .replace(/[а-яё]/g, (char) => {
    const map = { 'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ё':'yo','ж':'zh','з':'z','и':'i','й':'y','к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u','ф':'f','х':'h','ц':'ts','ч':'ch','ш':'sh','щ':'sch','ъ':'','ы':'y','ь':'','э':'e','ю':'yu','я':'ya' };
    return map[char] || char;
  })
  .replace(/[^\w\s-]/g, '')
  .replace(/[\s_-]+/g, '-')
  .replace(/^-+|-+$/g, '');

const ServiceModal = ({
  show,
  editingService,
  serviceForm,
  setServiceForm,
  categories,
  onSubmit,
  onClose,
}) => {
  const [tab, setTab] = useState('basic');

  if (!show) return null;

  // FAQ helpers
  const addFaqItem = (lang) => {
    const field = `faq_${lang}`;
    setServiceForm({ ...serviceForm, [field]: [...serviceForm[field], { question: '', answer: '' }] });
  };
  const updateFaqItem = (lang, index, key, value) => {
    const field = `faq_${lang}`;
    const newFaq = [...serviceForm[field]];
    newFaq[index] = { ...newFaq[index], [key]: value };
    setServiceForm({ ...serviceForm, [field]: newFaq });
  };
  const removeFaqItem = (lang, index) => {
    const field = `faq_${lang}`;
    setServiceForm({ ...serviceForm, [field]: serviceForm[field].filter((_, i) => i !== index) });
  };

  // List helpers (work_complex, required_docs)
  const addListItem = (field) => {
    setServiceForm({ ...serviceForm, [field]: [...serviceForm[field], ''] });
  };
  const updateListItem = (field, index, value) => {
    const newList = [...serviceForm[field]];
    newList[index] = value;
    setServiceForm({ ...serviceForm, [field]: newList });
  };
  const removeListItem = (field, index) => {
    setServiceForm({ ...serviceForm, [field]: serviceForm[field].filter((_, i) => i !== index) });
  };

  // SEO Variants
  const addSeoVariant = (lang) => {
    const field = `seo_variants_${lang}`;
    setServiceForm({
      ...serviceForm,
      [field]: [...serviceForm[field], { title: '', description: '', is_active: false }],
    });
  };
  const updateSeoVariant = (lang, index, key, value) => {
    const field = `seo_variants_${lang}`;
    const newVariants = [...serviceForm[field]];
    newVariants[index] = { ...newVariants[index], [key]: value };
    setServiceForm({ ...serviceForm, [field]: newVariants });
  };
  const removeSeoVariant = (lang, index) => {
    const field = `seo_variants_${lang}`;
    setServiceForm({ ...serviceForm, [field]: serviceForm[field].filter((_, i) => i !== index) });
  };
  const activateSeoVariant = (lang, index) => {
    const field = `seo_variants_${lang}`;
    const variant = serviceForm[field][index];
    setServiceForm({
      ...serviceForm,
      [`seo_title_${lang}`]: variant.title,
      [`seo_description_${lang}`]: variant.description,
    });
  };

  // SEO Extra Keywords
  const addSeoKeyword = (lang) => {
    const field = `seo_keywords_extra_${lang}`;
    setServiceForm({ ...serviceForm, [field]: [...serviceForm[field], ''] });
  };
  const updateSeoKeyword = (lang, index, value) => {
    const field = `seo_keywords_extra_${lang}`;
    const newKeywords = [...serviceForm[field]];
    newKeywords[index] = value;
    setServiceForm({ ...serviceForm, [field]: newKeywords });
  };
  const removeSeoKeyword = (lang, index) => {
    const field = `seo_keywords_extra_${lang}`;
    setServiceForm({ ...serviceForm, [field]: serviceForm[field].filter((_, i) => i !== index) });
  };

  const tabBtnCls = (key) =>
    `px-3 py-1.5 text-sm rounded-lg ${tab === key ? 'bg-orange-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">
              {editingService ? 'Редактировать услугу' : 'Новая услуга'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Название (RU) *</label>
                <input
                  type="text"
                  value={serviceForm.title_ru}
                  onChange={(e) => setServiceForm({
                    ...serviceForm,
                    title_ru: e.target.value,
                    slug: editingService ? serviceForm.slug : generateSlug(e.target.value),
                  })}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="service-title-ru"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Название (EN) *</label>
                <input
                  type="text"
                  value={serviceForm.title_en}
                  onChange={(e) => setServiceForm({ ...serviceForm, title_en: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="service-title-en"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Описание (RU) *</label>
                <textarea
                  value={serviceForm.description_ru}
                  onChange={(e) => setServiceForm({ ...serviceForm, description_ru: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="service-description-ru"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Описание (EN) *</label>
                <textarea
                  value={serviceForm.description_en}
                  onChange={(e) => setServiceForm({ ...serviceForm, description_en: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="service-description-en"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Сроки (RU) *</label>
                <input
                  type="text"
                  value={serviceForm.timeframe_ru}
                  onChange={(e) => setServiceForm({ ...serviceForm, timeframe_ru: e.target.value })}
                  required
                  placeholder="от 5 рабочих дней"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="service-timeframe-ru"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Сроки (EN) *</label>
                <input
                  type="text"
                  value={serviceForm.timeframe_en}
                  onChange={(e) => setServiceForm({ ...serviceForm, timeframe_en: e.target.value })}
                  required
                  placeholder="from 5 business days"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="service-timeframe-en"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Стоимость (RU)</label>
                <input
                  type="text"
                  value={serviceForm.price_ru}
                  onChange={(e) => setServiceForm({ ...serviceForm, price_ru: e.target.value })}
                  placeholder="от 15 000 ₽"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="service-price-ru"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Стоимость (EN)</label>
                <input
                  type="text"
                  value={serviceForm.price_en}
                  onChange={(e) => setServiceForm({ ...serviceForm, price_en: e.target.value })}
                  placeholder="from 15,000 RUB"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="service-price-en"
                />
              </div>
            </div>

            <div>
              <ImageUploader
                value={serviceForm.preview_image}
                onChange={(url) => setServiceForm({ ...serviceForm, preview_image: url })}
                label="Изображение услуги"
                maxSize={10}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Категория *</label>
                <select
                  value={serviceForm.category}
                  onChange={(e) => setServiceForm({ ...serviceForm, category: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="service-category-select"
                >
                  <option value="">Выберите категорию</option>
                  {categories.map((cat) => (
                    <option key={cat.key} value={cat.key}>{cat.name_ru}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL-slug *</label>
                <input
                  type="text"
                  value={serviceForm.slug}
                  onChange={(e) => setServiceForm({ ...serviceForm, slug: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  data-testid="service-slug"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={serviceForm.is_active}
                onChange={(e) => setServiceForm({ ...serviceForm, is_active: e.target.checked })}
                className="w-4 h-4 text-orange-500"
                data-testid="service-is-active-checkbox"
              />
              <label className="ml-2 text-sm text-gray-700">Услуга активна</label>
            </div>

            {/* Tab Switcher */}
            <div className="border-t pt-4 mt-4">
              <div className="flex space-x-2 mb-4">
                <button type="button" onClick={() => setTab('basic')} className={tabBtnCls('basic')} data-testid="tab-basic">Основное</button>
                <button type="button" onClick={() => setTab('content')} className={tabBtnCls('content')} data-testid="tab-content">Контент</button>
                <button type="button" onClick={() => setTab('faq')} className={tabBtnCls('faq')} data-testid="tab-faq">
                  FAQ ({serviceForm.faq_ru.length})
                </button>
                <button type="button" onClick={() => setTab('work')} className={tabBtnCls('work')} data-testid="tab-work">Комплекс работ</button>
                <button type="button" onClick={() => setTab('seo')} className={tabBtnCls('seo')} data-testid="tab-seo">SEO</button>
              </div>

              {tab === 'content' && (
                <ServiceContentTab serviceForm={serviceForm} setServiceForm={setServiceForm} />
              )}
              {tab === 'faq' && (
                <ServiceFaqTab
                  serviceForm={serviceForm}
                  addFaqItem={addFaqItem}
                  updateFaqItem={updateFaqItem}
                  removeFaqItem={removeFaqItem}
                />
              )}
              {tab === 'work' && (
                <ServiceWorkTab
                  serviceForm={serviceForm}
                  addListItem={addListItem}
                  updateListItem={updateListItem}
                  removeListItem={removeListItem}
                />
              )}
              {tab === 'seo' && (
                <ServiceSeoTab
                  serviceForm={serviceForm}
                  setServiceForm={setServiceForm}
                  addSeoVariant={addSeoVariant}
                  updateSeoVariant={updateSeoVariant}
                  removeSeoVariant={removeSeoVariant}
                  activateSeoVariant={activateSeoVariant}
                  addSeoKeyword={addSeoKeyword}
                  updateSeoKeyword={updateSeoKeyword}
                  removeSeoKeyword={removeSeoKeyword}
                />
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg" data-testid="service-cancel-btn">
                Отмена
              </button>
              <button type="submit" className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600" data-testid="service-submit-btn">
                {editingService ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { generateSlug };
export default ServiceModal;
